import { Component, OnDestroy } from '@angular/core';
import { PageState } from '@app-core/models/_enums';
import { AppState } from '@app-core/models/app-state';
import { IMetric } from '@app-core/models/metric';
import { IValidationResponse } from '@app-core/models/validation-response';
import { ApiService } from '@app-core/services/api.service';
import { ImageValidationService } from '@app-core/services/image-validation.service';
import { StateService } from '@app-core/services/state.service';
import { CardDetails } from '@app-pages/models/card-details';
import { FaceImage } from '@app-pages/models/face-image';
import { TranslateService } from '@ngx-translate/core';
import { FileUploader } from 'ng2-file-upload';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

@Component({
  selector: 'app-upload-page',
  templateUrl: './upload-page.component.html',
  styleUrls: ['./upload-page.component.css']
})
export class UploadPageComponent implements OnDestroy {
  isUploading: Observable<boolean>;
  imageRequirements: Observable<string[]>;
  imageState: Observable<FaceImage>;

  imageUploader: FileUploader;
  failingMetrics: IMetric[];
  imageInvalid: boolean;
  isLoading: boolean;
  savePermission: boolean;
  errorDetails: { show: boolean; textOrKey: string };

  subscriptions = new Subscription();

  constructor(
    private _apiService: ApiService,
    private _stateService: StateService,
    private _translate: TranslateService,
    private _imageValidation: ImageValidationService
  ) {
    this.imageUploader = _imageValidation.getImageUploader();
    this.errorDetails = { show: false, textOrKey: null };
    this.isUploading = this._imageValidation.onLoading$();
    this.imageRequirements = this._translate.get('UPLOAD-PAGE.IMAGE-REQUIREMENTS');
    this.imageState = this._stateService.getState$().pipe(
      filter((currentState: AppState) => currentState.cardDetails != null),
      map((currentState: AppState) => currentState.cardDetails),
      map((cardDetails: CardDetails) => {
        const { image } = cardDetails;
        this.savePermission = image != null && image.photoPermission;
        return image;
      })
    );

    this.subscriptions.add(
      this._imageValidation
        .onError$()
        .subscribe(() => (this.errorDetails = { show: true, textOrKey: 'GENERAL.ERROR' }))
    );

    this.subscriptions.add(
      this._imageValidation
        .onValidate$()
        .pipe(
          withLatestFrom(this._stateService.getState$()),
          map(([validationResult, currentState]) => {
            this.imageInvalid = !validationResult.compliant;
            this.failingMetrics = validationResult.failingMetrics;
            const { ref, engravedId } = currentState.cardDetails;
            return new CardDetails(engravedId, ref, validationResult.image);
          })
        )
        .subscribe((cardDetails: CardDetails) => {
          this._stateService.updateState({ cardDetails });
          this.errorDetails = { show: false, textOrKey: null };
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updateSavePermission(): void {
    this._stateService
      .getState$()
      .pipe(
        take(1),
        map((currentState: AppState) => {
          const { ref, engravedId, image } = currentState.cardDetails;
          return new CardDetails(
            engravedId,
            ref,
            new FaceImage(image.imageAsBase64, image.contentType, this.savePermission)
          );
        })
      )
      .subscribe((cardDetails: CardDetails) => this._stateService.updateState({ cardDetails }));
  }

  confirm(): void {
    this.isLoading = true;
    this._stateService
      .getState$()
      .pipe(
        take(1),
        switchMap((currentState: AppState) =>
          this._apiService.confirmCardDetails(currentState.cardDetails)
        )
      )
      .subscribe(
        (response: IValidationResponse) => {
          this.isLoading = false;
          this.errorDetails = {
            show: !response.succes,
            textOrKey: response.succes ? null : response.error.message
          };
          if (response.succes) {
            this._stateService.updateState({
              currentPageState: PageState.THANKS_PAGE
            });
          }
        },
        () => {
          this.isLoading = false;
          this.errorDetails = { show: true, textOrKey: 'GENERAL.ERROR' };
        }
      );
  }
}
